/**
 * Connections + skills state for the Plugins page.
 *
 * Modelled on `state/usage.ts`: the page reads a warm, cached atom
 * synchronously, so opening it is a memory read, never a fetch. The atom runs
 * an Effect that fetches the T3 server's `/api/amoreg/connections` endpoint and
 * decodes it with Schema; the reactivity runtime memoises the result, so
 * re-opening the page is instant and revalidation happens in the background.
 *
 * The server declares its `capabilities` as typed data. The page renders only
 * the actions the server supports — a button never appears for something the
 * server can't do.
 *
 * @module state/connections
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { useActiveOrgId } from "./orgs";
import { openConnectionConsent } from "./connectionConsent";

const ConnectionStatus = Schema.Literals(["connected", "disconnected", "pending", "error"]);

const ConnectionSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  description: Schema.String,
  iconUrl: Schema.NullOr(Schema.String),
  source: Schema.Literals(["composio", "native", "custom"]),
  capabilities: Schema.Array(Schema.String),
  connectionStatus: ConnectionStatus,
  userEnabled: Schema.Boolean,
  /**
   * Whether the server can broker connect/disconnect for THIS connector —
   * false when its contract (e.g. an API key the server doesn't hold) is
   * unfulfilled. Optional so older servers interop; absent means no.
   */
  connectable: Schema.optional(Schema.Boolean),
  /**
   * The provider's toolkit key. The icon fallback and the MCP server name on
   * the detail page key on it. Optional so an older server still decodes.
   */
  toolkitSlug: Schema.optional(Schema.NullOr(Schema.String)),
  /** Catalogue metadata: categories, developer, auth schemes. */
  metadata: Schema.optional(Schema.NullOr(Schema.Record(Schema.String, Schema.Unknown))),
  /** This user's connection metadata: account label, granted scopes. */
  userMetadata: Schema.optional(Schema.NullOr(Schema.Record(Schema.String, Schema.Unknown))),
  /** Whether this user keeps the connector in their plugin collection. */
  installed: Schema.optional(Schema.Boolean),
});
export type ConnectionView = typeof ConnectionSchema.Type;

const SkillSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  slug: Schema.String,
  description: Schema.String,
  scope: Schema.Literals(["global", "jurisdiction", "organization"]),
  category: Schema.NullOr(Schema.String),
});
export type SkillView = typeof SkillSchema.Type;

/** Typed statement of what the server can do; the UI renders off this alone. */
const CapabilitiesSchema = Schema.Struct({
  list: Schema.Boolean,
  connect: Schema.Boolean,
  disconnect: Schema.Boolean,
  toggle: Schema.Boolean,
  /** Add/remove from the user's plugin collection. Absent on older servers. */
  install: Schema.optional(Schema.Boolean),
});
export type ConnectionsCapabilities = typeof CapabilitiesSchema.Type;

const SnapshotSchema = Schema.Struct({
  connections: Schema.Array(ConnectionSchema),
  skills: Schema.Array(SkillSchema),
  capabilities: CapabilitiesSchema,
});
type Snapshot = typeof SnapshotSchema.Type;

/** No connections feature configured on this server (endpoint 404). */
const UNAVAILABLE: Snapshot = {
  connections: [],
  skills: [],
  capabilities: { list: false, connect: false, disconnect: false, toggle: false },
};

const decodeSnapshot = Schema.decodeUnknownEffect(SnapshotSchema);

class ConnectionsRequestError extends Schema.TaggedErrorClass<ConnectionsRequestError>()(
  "ConnectionsRequestError",
  { status: Schema.Number },
) {}

const fetchSnapshot = (activeOrgId: string | null) =>
  Effect.gen(function* () {
    // The multi-tenant server scopes connections per org; the last visited org
    // is the active one (recorded on every org landing).
    const url = resolvePrimaryEnvironmentHttpUrl(
      "/api/amoreg/connections",
      activeOrgId ? { org: activeOrgId } : undefined,
    );
    const json = yield* Effect.tryPromise(async (signal) => {
      const response = await fetch(url, {
        credentials: "include",
        signal: AbortSignal.any([signal, AbortSignal.timeout(20_000)]),
      });
      // A missing endpoint means the feature is unavailable, not a failed load.
      if (response.status === 404) return UNAVAILABLE;
      if (!response.ok) throw new ConnectionsRequestError({ status: response.status });
      return response.json();
    });
    return yield* decodeSnapshot(json);
  });

// Cache each org separately so switching tenants cannot reuse another org’s skills.
const connectionsRuntime = Atom.runtime(Layer.empty);
const connectionsByOrgAtom = Atom.family((orgId: string | null) =>
  connectionsRuntime
    .atom(fetchSnapshot(orgId))
    .pipe(Atom.withLabel(`web-amoreg:connections:${orgId ?? "default"}`)),
);

export interface ConnectionsView {
  readonly connections: readonly ConnectionView[];
  readonly skills: readonly SkillView[];
  readonly capabilities: ConnectionsCapabilities;
  /** False when the server has no connections feature. */
  readonly available: boolean;
  /** True until the first response arrives. */
  readonly isPending: boolean;
  /** True for the full lifetime of a read, including background refreshes. */
  readonly isRefreshing: boolean;
  readonly error: string | null;
  readonly refresh: () => void;
  /** Broker an OAuth link and open the consent screen. Resolves when opened. */
  readonly connect: (connectionId: string) => Promise<void>;
  /** Delete the connected account(s) for a connector. */
  readonly disconnect: (connectionId: string) => Promise<void>;
  /**
   * Drop the stale grant and run consent again — the fix for an account linked
   * before the auth config carried the product scopes.
   */
  readonly reconnect: (connectionId: string) => Promise<void>;
}

class ConnectionsActionError extends Schema.TaggedErrorClass<ConnectionsActionError>()(
  "ConnectionsActionError",
  { status: Schema.Number, detail: Schema.optional(Schema.String) },
) {
  override get message(): string {
    return this.detail ?? "The account change could not be completed. Please try again.";
  }
}

const ConnectLinkSchema = Schema.Struct({ redirectUrl: Schema.String });
const decodeConnectLink = Schema.decodeUnknownEffect(ConnectLinkSchema);

type ConnectionAction = "connect" | "disconnect" | "reconnect";

const postConnectionAction = (
  action: ConnectionAction,
  connectionId: string,
  activeOrgId: string | null,
) =>
  Effect.gen(function* () {
    const url = resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/connections/${action}`, {
      connection: connectionId,
      ...(activeOrgId ? { org: activeOrgId } : {}),
    });
    const response = yield* Effect.tryPromise(() =>
      fetch(url, { method: "POST", credentials: "include" }),
    ).pipe(Effect.mapError(() => new ConnectionsActionError({ status: 0 })));
    if (!response.ok) {
      const detail = yield* Effect.tryPromise(() => response.text()).pipe(
        Effect.orElseSucceed(() => ""),
      );
      return yield* Effect.fail(
        new ConnectionsActionError({
          status: response.status,
          detail: detail.slice(0, 500) || undefined,
        }),
      );
    }
    return response;
  });

/**
 * Broker the OAuth link and hand the consent screen to a new tab. The row is
 * pending server-side; refreshing on return (window focus) settles it.
 */
const connectEffect = (
  action: "connect" | "reconnect",
  connectionId: string,
  orgId: string | null,
) =>
  Effect.gen(function* () {
    const response = yield* postConnectionAction(action, connectionId, orgId);
    const json = yield* Effect.tryPromise(() => response.json()).pipe(
      Effect.mapError(() => new ConnectionsActionError({ status: 502 })),
    );
    const link = yield* decodeConnectLink(json).pipe(
      Effect.mapError(() => new ConnectionsActionError({ status: 502 })),
    );
    return link.redirectUrl;
  });

/**
 * Nothing loaded: what a surface reads while it has no reason to fetch. Typed
 * as one of the family's atoms so a conditional read keeps one value type.
 */
const idleConnectionsAtom = Atom.make(
  AsyncResult.initial<Snapshot, never>(),
) as unknown as ReturnType<typeof connectionsByOrgAtom>;

/**
 * The person's usable connected apps, fetched only while `enabled` — the
 * composer's `@` menu wants them while a mention is being typed, not on every
 * chat mount. Connected and switched on; pending, failed and disabled
 * connections are not offered.
 */
export function useConnectedConnections(enabled: boolean): readonly ConnectionView[] {
  const orgId = useActiveOrgId();
  const result = useAtomValue(enabled && orgId ? connectionsByOrgAtom(orgId) : idleConnectionsAtom);
  const snapshot = Option.getOrNull(AsyncResult.value(result));
  return useMemo(
    () =>
      (snapshot?.connections ?? []).filter(
        (connection) => connection.connectionStatus === "connected" && connection.userEnabled,
      ),
    [snapshot],
  );
}

/**
 * The organization's skill library (its own, its jurisdictions' and the shared
 * skills) for the composer's `$` menu and its skill chips. Same cached per-org
 * snapshot as the Plugins page, so it is one read per organization.
 */
export function useOrganizationSkills(enabled: boolean): readonly SkillView[] {
  const orgId = useActiveOrgId();
  const result = useAtomValue(enabled && orgId ? connectionsByOrgAtom(orgId) : idleConnectionsAtom);
  const snapshot = Option.getOrNull(AsyncResult.value(result));
  return snapshot?.skills ?? NO_SKILLS;
}

const NO_SKILLS: readonly SkillView[] = [];

/** Shared catalogue lookup for activity marks, including apps without a bundled fallback. */
export function useConnectionIcon(connectorId: string, enabled = true) {
  const orgId = useActiveOrgId();
  const result = useAtomValue(enabled && orgId ? connectionsByOrgAtom(orgId) : idleConnectionsAtom);
  const snapshot = Option.getOrNull(AsyncResult.value(result));
  const connection = snapshot?.connections.find((item) => item.id === connectorId);
  return connection?.iconUrl ?? null;
}

/** The colour family a connected app's catalogue row names, once the snapshot is loaded. */
export function useConnectionAccentFamily(connectorId: string, enabled: boolean): string | null {
  const orgId = useActiveOrgId();
  const result = useAtomValue(enabled && orgId ? connectionsByOrgAtom(orgId) : idleConnectionsAtom);
  const snapshot = Option.getOrNull(AsyncResult.value(result));
  const accent = snapshot?.connections.find((connection) => connection.id === connectorId)?.metadata
    ?.accent;
  return typeof accent === "string" && accent.length > 0 ? accent : null;
}

export function useConnections(): ConnectionsView {
  const orgId = useActiveOrgId();
  const connectionsAtom = connectionsByOrgAtom(orgId);
  const result = useAtomValue(connectionsAtom);
  const snapshot = Option.getOrNull(AsyncResult.value(result));
  const hasPending =
    snapshot?.connections.some((connection) => connection.connectionStatus === "pending") ?? false;
  const refresh = useCallback(() => {
    // Focus, visibility and manual reload can arrive together. Let the current
    // read finish instead of repeatedly cancelling it and starting again.
    if (!appAtomRegistry.get(connectionsAtom).waiting) appAtomRegistry.refresh(connectionsAtom);
  }, [connectionsAtom]);

  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [refresh]);

  useEffect(() => {
    if (!hasPending) return;
    // Focus refresh alone can arrive before the provider commits the grant.
    // Retry while visible, with one request at a time and a bounded lifetime.
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (Date.now() - startedAt > 11 * 60_000) {
        window.clearInterval(interval);
        return;
      }
      if (document.visibilityState === "visible") refresh();
    }, 3_000);
    return () => window.clearInterval(interval);
  }, [connectionsAtom, hasPending, refresh]);

  const runAction = useCallback(
    async (action: ConnectionAction, connectionId: string) => {
      try {
        if (action === "disconnect") {
          await Effect.runPromise(
            postConnectionAction(action, connectionId, orgId).pipe(Effect.asVoid),
          );
        } else {
          await openConnectionConsent({
            openTab: () => window.open("about:blank", "_blank"),
            getUrl: () => Effect.runPromise(connectEffect(action, connectionId, orgId)),
            navigate: (url) => {
              window.location.href = url;
            },
          });
        }
      } finally {
        // A write must invalidate even an in-flight read: it may predate the
        // account change. Its AbortSignal prevents that superseded read lingering.
        appAtomRegistry.refresh(connectionsAtom);
      }
    },
    [orgId, connectionsAtom],
  );

  const connect = useCallback((id: string) => runAction("connect", id), [runAction]);
  const reconnect = useCallback((id: string) => runAction("reconnect", id), [runAction]);
  const disconnect = useCallback((id: string) => runAction("disconnect", id), [runAction]);

  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    const capabilities =
      snapshot?.capabilities ??
      ({ list: false, connect: false, disconnect: false, toggle: false } as const);
    return {
      connections: snapshot?.connections ?? [],
      skills: snapshot?.skills ?? [],
      capabilities,
      available: capabilities.list,
      isPending: result.waiting && snapshot === null,
      isRefreshing: result.waiting,
      error: result._tag === "Failure" ? "Connections could not be loaded." : null,
      refresh,
      connect,
      disconnect,
      reconnect,
    };
  }, [result, refresh, connect, disconnect, reconnect]);
}
